/** @userstory US-SUB-02, US-SUB-03, US-FG-02, US-FG-10 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	FeatureNotAvailableResponse,
	FeatureLimitReachedResponse,
} from '../../common/errors/featureErrors';
import { FeatureNames } from '../../common/constants/featureCodes';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../bootstrap/Modal';
import Button from '../bootstrap/Button';
import Icon from '../icon/Icon';
import AuthContext from '../../contexts/authContext';
import { UserType } from '../../common/data/dummyRolesData';
import subscriptionService from '../../services/api/subscriptionService';

// Lazy import to avoid circular dependency
let useOrganization: () => { selectedOrganization: { id: string } | null };
try {
	useOrganization = require('../../contexts/organizationContext').useOrganization;
} catch {
	useOrganization = () => ({ selectedOrganization: null });
}

/**
 * Global component that listens to feature error events and shows modals.
 * Place this component once in your app, typically at the root level.
 */
/**
 * Global modal component that listens for feature error CustomEvents
 * ('feature-not-available' and 'feature-limit-reached') dispatched by the
 * apiClient interceptor. Shows appropriate modal UI and offers Stripe portal redirect.
 *
 * @userstory US-FG-02, US-FG-10
 */
const FeatureErrorModal: React.FC = () => {
	const { t } = useTranslation(['features', 'subscription', 'common']);
	const navigate = useNavigate();
	const { userData } = useContext(AuthContext);

	const [showNotAvailable, setShowNotAvailable] = useState(false);
	const [showLimitReached, setShowLimitReached] = useState(false);
	const [notAvailableData, setNotAvailableData] = useState<FeatureNotAvailableResponse | null>(
		null,
	);
	const [limitReachedData, setLimitReachedData] = useState<FeatureLimitReachedResponse | null>(
		null,
	);
	const [redirecting, setRedirecting] = useState(false);

	// Try to get organization context (only available in company pages)
	let organizationId: string | null = null;
	try {
		const { selectedOrganization } = useOrganization();
		organizationId = selectedOrganization?.id || null;
	} catch {
		organizationId = null;
	}

	// Handle feature not available event
	const handleNotAvailable = useCallback((event: CustomEvent<FeatureNotAvailableResponse>) => {
		setNotAvailableData(event.detail);
		setShowNotAvailable(true);
	}, []);

	// Handle feature limit reached event
	const handleLimitReached = useCallback((event: CustomEvent<FeatureLimitReachedResponse>) => {
		setLimitReachedData(event.detail);
		setShowLimitReached(true);
	}, []);

	// Subscribe to events
	useEffect(() => {
		window.addEventListener('feature-not-available', handleNotAvailable as EventListener);
		window.addEventListener('feature-limit-reached', handleLimitReached as EventListener);

		return () => {
			window.removeEventListener(
				'feature-not-available',
				handleNotAvailable as EventListener,
			);
			window.removeEventListener(
				'feature-limit-reached',
				handleLimitReached as EventListener,
			);
		};
	}, [handleNotAvailable, handleLimitReached]);

	const handleUpgrade = useCallback(async () => {
		// Determine if user is company or freelance and redirect accordingly
		const isCompany = userData?.role === UserType.COMPANY;
		const isFreelance = userData?.role === UserType.FREELANCE;

		try {
			setRedirecting(true);

			if (isFreelance) {
				// Navigate to talent subscription page
				setShowNotAvailable(false);
				setShowLimitReached(false);
				navigate('/talent/settings/subscription');
			} else if (isCompany && organizationId) {
				// Redirect to Stripe Portal for organization
				await subscriptionService.redirectToOrganizationPortal(organizationId);
			} else {
				// Fallback: navigate to subscription page
				setShowNotAvailable(false);
				setShowLimitReached(false);
				navigate('/company/subscription');
			}
		} catch (err) {
			console.error('Failed to redirect to portal:', err);
			setRedirecting(false);
			// Fallback to subscription page on error
			setShowNotAvailable(false);
			setShowLimitReached(false);
			const fallbackPath = userData?.role === UserType.FREELANCE ? '/talent/settings/subscription' : '/company/subscription';
			navigate(fallbackPath);
		}
	}, [userData, organizationId, navigate]);

	const getFeatureName = (code: string): string => {
		// Return default text if code is empty or undefined
		if (!code) {
			return t('features.upgrade.thisFeature', 'This feature');
		}
		return t(`features.names.${code}`, FeatureNames[code as keyof typeof FeatureNames] || code);
	};

	const formatResetDate = (isoDate: string): string => {
		try {
			const date = new Date(isoDate);
			return date.toLocaleDateString();
		} catch {
			return isoDate;
		}
	};

	return (
		<>
			{/* Feature Not Available Modal */}
			<Modal
				isOpen={showNotAvailable}
				setIsOpen={setShowNotAvailable}
				titleId='feature-not-available-modal'
				isCentered>
				<ModalHeader setIsOpen={setShowNotAvailable}>
					<Icon icon='Lock' className='me-2' />
					{t('features.upgrade.upgradeTitle', 'Upgrade Required')}
				</ModalHeader>
				<ModalBody className='text-center py-4'>
					<div className='mb-3'>
						<Icon icon='RocketLaunch' size='3x' className='text-primary' />
					</div>
					<p className='lead mb-2'>{getFeatureName(notAvailableData?.feature || '')}</p>
					<p className='text-muted'>
						{t('features.upgrade.notAvailable', {
							feature: getFeatureName(notAvailableData?.feature || ''),
							defaultValue: `${getFeatureName(notAvailableData?.feature || '')} is available on Paid plan. Upgrade to unlock this feature.`,
						})}
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						color='primary'
						icon='OpenInNew'
						onClick={handleUpgrade}
						isDisable={redirecting}>
						{redirecting ? (
							<>
								<span className='spinner-border spinner-border-sm me-2' />
								{t('subscription.redirecting', 'Redirecting...')}
							</>
						) : (
							t('subscription.manageSubscription', 'Manage Subscription')
						)}
					</Button>
				</ModalFooter>
			</Modal>

			{/* Feature Limit Reached Modal */}
			<Modal
				isOpen={showLimitReached}
				setIsOpen={setShowLimitReached}
				titleId='feature-limit-reached-modal'
				isCentered>
				<ModalHeader setIsOpen={setShowLimitReached}>
					<Icon icon='Warning' className='me-2 text-warning' />
					{t('features.upgrade.limitReachedTitle', 'Limit Reached')}
				</ModalHeader>
				<ModalBody className='text-center py-4'>
					<div className='mb-3'>
						<Icon icon='PieChart' size='3x' className='text-warning' />
					</div>
					<p className='lead mb-2'>{getFeatureName(limitReachedData?.feature || '')}</p>
					<p className='text-muted'>
						{t('features.upgrade.limitReached', {
							feature: getFeatureName(limitReachedData?.feature || ''),
							defaultValue: `You've used all your ${getFeatureName(limitReachedData?.feature || '')} for this month.`,
						})}
					</p>
					{limitReachedData && (
						<div className='d-flex justify-content-center gap-4 my-3'>
							<div>
								<div className='h4 mb-0 text-danger'>{limitReachedData.used}</div>
								<small className='text-muted'>{t('Used')}</small>
							</div>
							<div>
								<div className='h4 mb-0'>{limitReachedData.limit}</div>
								<small className='text-muted'>{t('Limit')}</small>
							</div>
						</div>
					)}
					{limitReachedData?.resetsAt && (
						<p className='small text-muted'>
							{t('features.upgrade.resetInfo', {
								date: formatResetDate(limitReachedData.resetsAt),
								defaultValue: `Your limit resets on ${formatResetDate(limitReachedData.resetsAt)}`,
							})}
						</p>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						color='primary'
						icon='OpenInNew'
						onClick={handleUpgrade}
						isDisable={redirecting}>
						{redirecting ? (
							<>
								<span className='spinner-border spinner-border-sm me-2' />
								{t('subscription.redirecting', 'Redirecting...')}
							</>
						) : (
							t('subscription.manageSubscription', 'Manage Subscription')
						)}
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default FeatureErrorModal;
